import React, { useState, useRef, useEffect } from "react";
import {
  Avatar, Box, Chip, Divider, IconButton, MenuItem, Paper,
  Select, Stack, TextField, Tooltip, Typography, alpha,
  useMediaQuery, useTheme,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import SmsIcon from "@mui/icons-material/Sms";
import PhoneIcon from "@mui/icons-material/Phone";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import {
  useGetInboxConversationsQuery, useGetInboxConversationQuery, useReplyInboxMutation,
  useAssignInboxMutation, useSetInboxStatusMutation, useAddInboxNoteMutation,
  useAddInboxTagMutation, useRemoveInboxTagMutation, useGetCompanyUsersQuery,
} from "../../Api/platformApi";
import { StatusBadge, SearchBox, EmptyState, ActionButton, CardSurface } from "../../Components/ui";
import { NAVY, GOLD, PAGE_BG, BORDER, TEXT_SECONDARY, NAVY_DARK } from "../../theme/tokens";

/* ─── helpers ─────────────────────────────────────────────────────── */

const timeAgo = (d?: string) => {
  if (!d) return "";
  const date = new Date(d);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString();
};

const formatTime = (d?: string) =>
  d ? new Date(d).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";

const dateLabel = (d?: string) =>
  d
    ? new Date(d).toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" })
    : "";

const sameDay = (a?: string, b?: string) => {
  if (!a || !b) return false;
  return new Date(a).toDateString() === new Date(b).toDateString();
};

const avatarInitial = (name?: string, phone?: string) =>
  (name || phone || "?").charAt(0).toUpperCase();

const avatarColor = (s?: string) => {
  const palette = [
    "#4F46E5", "#0891B2", "#059669", "#D97706", "#DC2626", "#7C3AED", "#DB2777",
  ];
  const c = (s || "").charCodeAt(0) % palette.length;
  return palette[c];
};

const ChannelIcon: React.FC<{ channel?: string; size?: number }> = ({ channel, size = 14 }) => {
  if (channel === "whatsapp")
    return <WhatsAppIcon sx={{ fontSize: size, color: "#25D366" }} />;
  if (channel === "voice")
    return <PhoneIcon sx={{ fontSize: size, color: GOLD }} />;
  return <SmsIcon sx={{ fontSize: size, color: "#3B82F6" }} />;
};

/* ─── ThreadPanel ──────────────────────────────────────────────────── */

const ThreadPanel: React.FC<{ id: number; onBack?: () => void }> = ({ id, onBack }) => {
  const { data, isLoading } = useGetInboxConversationQuery(id, { pollingInterval: 8000 });
  const { data: usersData } = useGetCompanyUsersQuery();
  const [reply, { isLoading: replying }] = useReplyInboxMutation();
  const [assign] = useAssignInboxMutation();
  const [setStatus] = useSetInboxStatusMutation();
  const [addNote] = useAddInboxNoteMutation();
  const [addTag] = useAddInboxTagMutation();
  const [removeTag] = useRemoveInboxTagMutation();

  const [channel, setChannel] = useState("sms");
  const [text, setText] = useState("");
  const [note, setNote] = useState("");
  const [tag, setTag] = useState("");

  const scrollRef = useRef<HTMLDivElement>(null);

  const conv = data?.data?.conversation;
  const messages = data?.data?.messages ?? [];
  const notes = data?.data?.notes ?? [];
  const members = usersData?.data ?? [];

  React.useEffect(() => {
    if (conv?.last_channel) setChannel(conv.last_channel);
  }, [conv?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  if (isLoading || !conv)
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: TEXT_SECONDARY }}>
        <Typography>Loading…</Typography>
      </Box>
    );

  const send = async () => {
    if (!text.trim()) return;
    await reply({ id, channel, message: text }).unwrap().catch(() => {});
    setText("");
  };

  const bgColor = avatarColor(conv.customer_name || conv.customer_phone);

  return (
    <Stack sx={{ height: "100%", overflow: "hidden" }}>
      {/* ── Header ── */}
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          borderBottom: `1px solid ${BORDER}`,
          bgcolor: "#fff",
          flexShrink: 0,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          {onBack && (
            <Tooltip title="Back to list">
              <IconButton size="small" onClick={onBack} sx={{ color: NAVY }}>
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: bgColor,
              fontWeight: 700,
              fontSize: 16,
              flexShrink: 0,
            }}
          >
            {avatarInitial(conv.customer_name, conv.customer_phone)}
          </Avatar>
          <Box flexGrow={1} minWidth={0}>
            <Typography fontWeight={700} fontSize={15} color={NAVY} noWrap>
              {conv.customer_name || conv.customer_phone}
            </Typography>
            {conv.customer_name && (
              <Typography variant="caption" color={TEXT_SECONDARY} noWrap>
                {conv.customer_phone}
              </Typography>
            )}
          </Box>

          {/* Assignment */}
          <Select
            size="small"
            value={conv.assigned_user_id ?? ""}
            displayEmpty
            onChange={(e) => assign({ id, assigned_user_id: (e.target.value as any) || null })}
            sx={{
              minWidth: 140,
              fontSize: 13,
              borderRadius: "8px",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
            }}
          >
            <MenuItem value=""><em>Unassigned</em></MenuItem>
            {members.map((m: any) => (
              <MenuItem key={m.user?.id} value={m.user?.id}>
                {m.user?.name || m.user?.username}
              </MenuItem>
            ))}
          </Select>

          {/* Status badge */}
          <StatusBadge status={conv.status} />

          {/* Reopen / Close */}
          {conv.status === "closed" ? (
            <ActionButton
              tone="navy"
              size="small"
              startIcon={<LockOpenIcon fontSize="small" />}
              onClick={() => setStatus({ id, status: "open" })}
              sx={{ fontSize: 12, py: 0.5, px: 1.5 }}
            >
              Reopen
            </ActionButton>
          ) : (
            <ActionButton
              tone="gold"
              size="small"
              startIcon={<CloseIcon fontSize="small" />}
              onClick={() => setStatus({ id, status: "closed" })}
              sx={{ fontSize: 12, py: 0.5, px: 1.5 }}
            >
              Close
            </ActionButton>
          )}
        </Stack>

        {/* Tags row */}
        <Stack direction="row" spacing={0.75} mt={1.25} flexWrap="wrap" alignItems="center">
          {(conv.tags ?? []).map((t: any) => (
            <Chip
              key={t.id}
              size="small"
              label={t.tag}
              variant="outlined"
              onDelete={() => removeTag({ id, tagId: t.id })}
              sx={{
                fontSize: 11,
                height: 22,
                borderColor: alpha(NAVY, 0.2),
                color: NAVY,
                "& .MuiChip-deleteIcon": { fontSize: 13, color: alpha(NAVY, 0.4) },
              }}
            />
          ))}
          <TextField
            size="small"
            placeholder="+ tag"
            value={tag}
            onChange={(e) => setTag(e.target.value)}
            onKeyDown={async (e) => {
              if (e.key === "Enter" && tag.trim()) {
                await addTag({ id, tag });
                setTag("");
              }
            }}
            sx={{
              width: 90,
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                fontSize: 11,
                height: 24,
                "& fieldset": { borderColor: alpha(NAVY, 0.15) },
              },
            }}
            InputProps={{
              endAdornment: tag.trim() ? (
                <IconButton
                  size="small"
                  sx={{ p: 0.25 }}
                  onClick={async () => { await addTag({ id, tag }); setTag(""); }}
                >
                  <AddIcon sx={{ fontSize: 12, color: GOLD }} />
                </IconButton>
              ) : undefined,
            }}
          />
        </Stack>
      </Box>

      {/* ── Messages ── */}
      <Box
        ref={scrollRef}
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          px: 2.5,
          py: 2,
          bgcolor: PAGE_BG,
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        {messages.length === 0 && (
          <Box sx={{ my: "auto" }}>
            <EmptyState
              icon={<ForumOutlinedIcon />}
              title="No messages yet"
              message="Send the first message to start the conversation."
              dense
            />
          </Box>
        )}

        {messages.map((m: any, idx: number) => {
          const prev = idx > 0 ? messages[idx - 1] : null;
          const showDate = !sameDay(prev?.created_at, m.created_at);
          const out = m.direction === "outbound";
          const isVoice = m.source_type === "voice";

          return (
            <React.Fragment key={`${m.source_type}-${m.id}`}>
              {showDate && (
                <Box sx={{ display: "flex", alignItems: "center", my: 1.5, gap: 1.5 }}>
                  <Box sx={{ flex: 1, height: "1px", bgcolor: BORDER }} />
                  <Typography
                    fontSize={11}
                    fontWeight={600}
                    color={TEXT_SECONDARY}
                    sx={{
                      bgcolor: PAGE_BG,
                      px: 1.5,
                      py: 0.5,
                      borderRadius: "20px",
                      border: `1px solid ${BORDER}`,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {dateLabel(m.created_at)}
                  </Typography>
                  <Box sx={{ flex: 1, height: "1px", bgcolor: BORDER }} />
                </Box>
              )}

              {isVoice ? (
                /* Voice call pill — centered */
                <Box sx={{ display: "flex", justifyContent: "center", my: 0.5 }}>
                  <Stack
                    direction="row"
                    spacing={0.75}
                    alignItems="center"
                    sx={{
                      px: 2,
                      py: 0.75,
                      borderRadius: "20px",
                      bgcolor: alpha(GOLD, 0.1),
                      border: `1px solid ${alpha(GOLD, 0.25)}`,
                    }}
                  >
                    <PhoneIcon sx={{ fontSize: 14, color: GOLD }} />
                    <Typography fontSize={12} color={NAVY} fontWeight={600}>
                      {out ? "Outbound call" : "Inbound call"}
                    </Typography>
                    {m.status && (
                      <StatusBadge status={m.status} size="small" />
                    )}
                    <Typography fontSize={11} color={TEXT_SECONDARY}>
                      {formatTime(m.created_at)}
                    </Typography>
                  </Stack>
                </Box>
              ) : (
                /* Chat bubble */
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: out ? "flex-end" : "flex-start",
                    mb: 0.25,
                  }}
                >
                  <Box sx={{ maxWidth: "68%", display: "flex", flexDirection: "column", alignItems: out ? "flex-end" : "flex-start" }}>
                    <Paper
                      elevation={0}
                      sx={{
                        px: 1.75,
                        py: 1.1,
                        borderRadius: out ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                        background: out
                          ? `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_DARK} 100%)`
                          : "#fff",
                        color: out ? "#fff" : NAVY,
                        border: out ? "none" : `1px solid ${BORDER}`,
                        boxShadow: out ? "0 4px 12px rgba(11,30,45,0.18)" : "0 1px 4px rgba(0,0,0,0.06)",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: "pre-wrap", fontSize: 13.5, lineHeight: 1.55 }}
                      >
                        {m.body}
                      </Typography>
                    </Paper>
                    <Stack
                      direction="row"
                      spacing={0.75}
                      alignItems="center"
                      sx={{ mt: 0.4, px: 0.5 }}
                    >
                      <ChannelIcon channel={m.source_type} size={11} />
                      <Typography fontSize={10.5} color={TEXT_SECONDARY}>
                        {formatTime(m.created_at)}
                      </Typography>
                      {m.status && (
                        <Typography fontSize={10.5} color={TEXT_SECONDARY} sx={{ opacity: 0.75 }}>
                          · {m.status}
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                </Box>
              )}
            </React.Fragment>
          );
        })}
      </Box>

      {/* ── Reply composer ── */}
      <Box
        sx={{
          px: 2,
          pt: 1.5,
          pb: 1.5,
          borderTop: `1px solid ${BORDER}`,
          bgcolor: "#fff",
          flexShrink: 0,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="flex-end">
          <Select
            size="small"
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
            sx={{
              width: 128,
              fontSize: 13,
              borderRadius: "10px",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
            }}
          >
            <MenuItem value="sms">
              <Stack direction="row" spacing={0.75} alignItems="center">
                <SmsIcon sx={{ fontSize: 14, color: "#3B82F6" }} />
                <span>SMS</span>
              </Stack>
            </MenuItem>
            <MenuItem value="whatsapp">
              <Stack direction="row" spacing={0.75} alignItems="center">
                <WhatsAppIcon sx={{ fontSize: 14, color: "#25D366" }} />
                <span>WhatsApp</span>
              </Stack>
            </MenuItem>
          </Select>

          <TextField
            fullWidth
            size="small"
            placeholder="Type a reply… (Enter to send, Shift+Enter for newline)"
            value={text}
            multiline
            maxRows={4}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                fontSize: 13.5,
                "& fieldset": { borderColor: BORDER },
                "&:hover fieldset": { borderColor: alpha(NAVY, 0.35) },
                "&.Mui-focused fieldset": { borderColor: NAVY },
              },
            }}
          />

          <ActionButton
            tone="gold"
            endIcon={<SendIcon fontSize="small" />}
            onClick={send}
            disabled={replying || !text.trim()}
            sx={{ minWidth: 88, py: 1, flexShrink: 0, fontSize: 13 }}
          >
            Send
          </ActionButton>
        </Stack>

        {/* Internal note row */}
        <Stack direction="row" spacing={1} mt={1} alignItems="center">
          <NoteAddOutlinedIcon sx={{ fontSize: 16, color: TEXT_SECONDARY, flexShrink: 0 }} />
          <TextField
            size="small"
            placeholder="Add internal note…"
            value={note}
            fullWidth
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={async (e) => {
              if (e.key === "Enter" && !e.shiftKey && note.trim()) {
                e.preventDefault();
                await addNote({ id, note });
                setNote("");
              }
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                fontSize: 12.5,
                bgcolor: alpha(GOLD, 0.04),
                "& fieldset": { borderColor: alpha(GOLD, 0.3) },
                "&:hover fieldset": { borderColor: GOLD },
                "&.Mui-focused fieldset": { borderColor: GOLD },
              },
            }}
          />
          <ActionButton
            tone="navy"
            size="small"
            disabled={!note.trim()}
            onClick={async () => { await addNote({ id, note }); setNote(""); }}
            sx={{ fontSize: 12, py: 0.6, px: 1.5, flexShrink: 0 }}
          >
            Note
          </ActionButton>
        </Stack>
      </Box>
    </Stack>
  );
};

/* ─── CustomerProfile (right pane) ────────────────────────────────── */

const CustomerProfile: React.FC<{ id: number }> = ({ id }) => {
  const { data } = useGetInboxConversationQuery(id, { pollingInterval: 8000 });
  const conv = data?.data?.conversation;
  const notes = data?.data?.notes ?? [];

  if (!conv) return null;
  const bgColor = avatarColor(conv.customer_name || conv.customer_phone);

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      {/* Identity card */}
      <CardSurface noHover sx={{ p: 2.5, textAlign: "center" }}>
        <Avatar
          sx={{
            width: 56,
            height: 56,
            bgcolor: bgColor,
            fontSize: 22,
            fontWeight: 700,
            mx: "auto",
            mb: 1.25,
          }}
        >
          {avatarInitial(conv.customer_name, conv.customer_phone)}
        </Avatar>
        <Typography fontWeight={700} fontSize={15} color={NAVY}>
          {conv.customer_name || conv.customer_phone}
        </Typography>
        {conv.customer_name && (
          <Typography fontSize={12} color={TEXT_SECONDARY} mt={0.25}>
            {conv.customer_phone}
          </Typography>
        )}
        <Box mt={1.25}>
          <StatusBadge status={conv.status} />
        </Box>
      </CardSurface>

      {/* Tags */}
      {(conv.tags ?? []).length > 0 && (
        <CardSurface noHover sx={{ p: 2 }}>
          <Typography fontSize={11} fontWeight={700} color={TEXT_SECONDARY} mb={1} sx={{ textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Tags
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.75}>
            {(conv.tags ?? []).map((t: any) => (
              <Chip
                key={t.id}
                label={t.tag}
                size="small"
                sx={{
                  fontSize: 11,
                  height: 22,
                  bgcolor: alpha(NAVY, 0.06),
                  color: NAVY,
                  fontWeight: 600,
                }}
              />
            ))}
          </Stack>
        </CardSurface>
      )}

      {/* Notes */}
      <CardSurface noHover sx={{ p: 2 }}>
        <Typography fontSize={11} fontWeight={700} color={TEXT_SECONDARY} mb={1.25} sx={{ textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Internal Notes
        </Typography>
        {notes.length === 0 && (
          <Typography fontSize={12} color={TEXT_SECONDARY} sx={{ fontStyle: "italic" }}>
            No notes yet.
          </Typography>
        )}
        <Stack spacing={1}>
          {notes.map((n: any) => (
            <Box
              key={n.id}
              sx={{
                p: 1.25,
                borderRadius: "8px",
                bgcolor: alpha(GOLD, 0.06),
                border: `1px solid ${alpha(GOLD, 0.2)}`,
              }}
            >
              <Typography fontSize={12.5} color={NAVY} sx={{ lineHeight: 1.5 }}>
                {n.note}
              </Typography>
              <Typography fontSize={11} color={TEXT_SECONDARY} mt={0.5}>
                — {n.user?.name || n.user?.username}
              </Typography>
            </Box>
          ))}
        </Stack>
      </CardSurface>
    </Stack>
  );
};

/* ─── Inbox (main page) ────────────────────────────────────────────── */

const Inbox: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isWide = useMediaQuery(theme.breakpoints.up("lg"));

  const [filters, setFilters] = useState<any>({ search: "", status: "", unread: false, channel: "" });
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const queryArgs = {
    ...filters,
    unread: filters.unread ? 1 : "",
  };
  const { data, isLoading } = useGetInboxConversationsQuery(queryArgs, { pollingInterval: 15000 });
  const conversations = data?.data ?? [];

  /* ── Left pane: conversation list ── */
  const conversationList = (
    <Stack sx={{ height: "100%", overflow: "hidden" }}>
      {/* List header */}
      <Box sx={{ px: 2, pt: 2, pb: 1.5, flexShrink: 0 }}>
        <Typography
          fontSize={18}
          fontWeight={800}
          color={NAVY}
          mb={1.5}
          sx={{ letterSpacing: "-0.01em" }}
        >
          Inbox
        </Typography>
        <SearchBox
          value={filters.search}
          onChange={(v) => setFilters({ ...filters, search: v })}
          placeholder="Search name or phone…"
          width="100%"
        />
        {/* Filters row */}
        <Stack direction="row" spacing={0.75} mt={1.25} flexWrap="wrap" gap={0.75}>
          <Select
            size="small"
            value={filters.status}
            displayEmpty
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            sx={{
              flex: 1,
              minWidth: 100,
              fontSize: 12,
              borderRadius: "8px",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
            }}
          >
            <MenuItem value=""><em>All status</em></MenuItem>
            <MenuItem value="open">Open</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="closed">Closed</MenuItem>
          </Select>
          <Select
            size="small"
            value={filters.channel}
            displayEmpty
            onChange={(e) => setFilters({ ...filters, channel: e.target.value })}
            sx={{
              flex: 1,
              minWidth: 100,
              fontSize: 12,
              borderRadius: "8px",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
            }}
          >
            <MenuItem value=""><em>All channels</em></MenuItem>
            <MenuItem value="sms">SMS</MenuItem>
            <MenuItem value="whatsapp">WhatsApp</MenuItem>
          </Select>
          <Box
            onClick={() => setFilters({ ...filters, unread: !filters.unread })}
            sx={{
              px: 1.5,
              py: 0.5,
              borderRadius: "8px",
              border: `1px solid ${filters.unread ? NAVY : BORDER}`,
              bgcolor: filters.unread ? NAVY : "transparent",
              color: filters.unread ? "#fff" : TEXT_SECONDARY,
              fontSize: 12,
              fontWeight: filters.unread ? 700 : 400,
              cursor: "pointer",
              userSelect: "none",
              display: "flex",
              alignItems: "center",
              transition: "all 0.15s",
              "&:hover": { borderColor: NAVY, color: filters.unread ? "#fff" : NAVY },
            }}
          >
            Unread
          </Box>
        </Stack>
      </Box>

      <Divider sx={{ borderColor: BORDER }} />

      {/* Conversation rows */}
      <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
        {isLoading && (
          <Box sx={{ p: 2.5, color: TEXT_SECONDARY }}>
            <Typography fontSize={13}>Loading conversations…</Typography>
          </Box>
        )}

        {!isLoading && conversations.length === 0 && (
          <EmptyState
            icon={<ForumOutlinedIcon />}
            title="No conversations"
            message="Try adjusting your filters or check back later."
            dense
          />
        )}

        {conversations.map((c: any) => {
          const isActive = selectedId === c.id;
          const initBg = avatarColor(c.customer_name || c.customer_phone);

          return (
            <Box
              key={c.id}
              onClick={() => setSelectedId(c.id)}
              sx={{
                position: "relative",
                px: 2,
                py: 1.5,
                cursor: "pointer",
                borderBottom: `1px solid ${BORDER}`,
                bgcolor: isActive ? alpha(NAVY, 0.04) : "transparent",
                transition: "background 0.12s",
                "&:hover": { bgcolor: isActive ? alpha(NAVY, 0.06) : alpha(NAVY, 0.025) },
                /* Gold left accent for active item */
                "&::before": {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  top: 6,
                  bottom: 6,
                  width: 3,
                  borderRadius: "0 3px 3px 0",
                  bgcolor: isActive ? GOLD : "transparent",
                  transition: "background 0.12s",
                },
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                {/* Avatar with unread badge */}
                <Box sx={{ position: "relative", flexShrink: 0 }}>
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: initBg,
                      fontSize: 16,
                      fontWeight: 700,
                      mt: 0.25,
                    }}
                  >
                    {avatarInitial(c.customer_name, c.customer_phone)}
                  </Avatar>
                  {c.unread_count > 0 && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: -2,
                        right: -2,
                        minWidth: 16,
                        height: 16,
                        borderRadius: "8px",
                        bgcolor: "#EF4444",
                        color: "#fff",
                        fontSize: 10,
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        px: 0.5,
                        border: "1.5px solid #fff",
                      }}
                    >
                      {c.unread_count > 9 ? "9+" : c.unread_count}
                    </Box>
                  )}
                </Box>

                {/* Body */}
                <Box flexGrow={1} minWidth={0}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.25}>
                    <Typography
                      fontSize={13.5}
                      fontWeight={c.unread_count ? 700 : 600}
                      color={NAVY}
                      noWrap
                      sx={{ maxWidth: "60%" }}
                    >
                      {c.customer_name || c.customer_phone}
                    </Typography>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      {c.last_channel && <ChannelIcon channel={c.last_channel} size={13} />}
                      <Typography fontSize={11} color={TEXT_SECONDARY}>
                        {timeAgo(c.last_message_at)}
                      </Typography>
                    </Stack>
                  </Stack>
                  <Typography
                    fontSize={12.5}
                    color={TEXT_SECONDARY}
                    noWrap
                    sx={{ opacity: c.unread_count ? 1 : 0.8 }}
                  >
                    {c.last_message || "No messages yet"}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          );
        })}
      </Box>
    </Stack>
  );

  /* ── Center pane ── */
  const threadPane = selectedId ? (
    <ThreadPanel id={selectedId} onBack={isMobile ? () => setSelectedId(null) : undefined} />
  ) : (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        bgcolor: PAGE_BG,
      }}
    >
      <EmptyState
        icon={<ForumOutlinedIcon />}
        title="Select a conversation"
        message="Pick a conversation from the list to view the thread and reply."
      />
    </Box>
  );

  /* ── Right pane ── */
  const profilePane = selectedId && isWide ? (
    <Box
      sx={{
        width: 280,
        flexShrink: 0,
        borderLeft: `1px solid ${BORDER}`,
        overflowY: "auto",
        bgcolor: PAGE_BG,
      }}
    >
      <Box sx={{ px: 2, py: 1.75, borderBottom: `1px solid ${BORDER}`, bgcolor: "#fff" }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <PersonOutlineIcon sx={{ fontSize: 16, color: NAVY }} />
          <Typography fontSize={12} fontWeight={700} color={NAVY} sx={{ textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Customer
          </Typography>
        </Stack>
      </Box>
      <CustomerProfile id={selectedId} />
    </Box>
  ) : null;

  /* ── Outer shell ── */
  return (
    <Box
      sx={{
        height: "calc(100vh - 120px)",
        display: "flex",
        border: `1px solid ${BORDER}`,
        borderRadius: "16px",
        overflow: "hidden",
        bgcolor: "#fff",
        boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
      }}
    >
      {isMobile ? (
        selectedId ? (
          <Box sx={{ flex: 1, overflow: "hidden" }}>{threadPane}</Box>
        ) : (
          <Box sx={{ flex: 1, overflow: "hidden" }}>{conversationList}</Box>
        )
      ) : (
        <>
          {/* Left: conversation list */}
          <Box
            sx={{
              width: 320,
              flexShrink: 0,
              borderRight: `1px solid ${BORDER}`,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {conversationList}
          </Box>

          {/* Center: thread */}
          <Box sx={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
            {threadPane}
          </Box>

          {/* Right: customer profile */}
          {profilePane}
        </>
      )}
    </Box>
  );
};

export default Inbox;
